import type { ConnectionState } from '../api/api-client.js';

const TEXT: Record<ConnectionState, string> = {
  online: '',
  offline: 'Нет интернета',
  'server-down': 'Сервер недоступен',
};

/**
 * Ненавязчивый индикатор соединения на всех экранах (SPEC.md, 17.4).
 * «Нет интернета» — по признаку браузера, «Сервер недоступен» — по ответам API.
 */
export class ConnectionIndicator {
  private readonly element = document.createElement('div');
  private state: ConnectionState = 'online';
  private readonly listeners = new Set<(state: ConnectionState) => void>();
  /** Экран показывает состояние сам (бейдж на главном экране) — общий индикатор скрыт. */
  private ownedByScreen = false;

  constructor() {
    Object.assign(this.element.style, {
      position: 'fixed',
      top: '10px',
      right: '10px',
      padding: '6px 12px',
      borderRadius: '10px',
      background: 'rgba(120, 20, 50, 0.85)',
      color: '#ffe3ec',
      font: '600 14px system-ui, sans-serif',
      pointerEvents: 'none',
      zIndex: '20',
      display: 'none',
    });
    this.element.setAttribute('role', 'status');
    document.body.appendChild(this.element);
    window.addEventListener('offline', () => this.set('offline'));
    window.addEventListener('online', () => {
      if (this.state === 'offline') this.set('online');
    });
    if (navigator.onLine === false) this.set('offline');
  }

  set(state: ConnectionState): void {
    this.state = state;
    this.element.textContent = TEXT[state];
    this.render();
    this.listeners.forEach((listener) => listener(state));
  }

  /** Подписка экрана на состояние; пока подписка жива, общий индикатор скрыт. */
  watch(listener: (state: ConnectionState) => void): () => void {
    this.listeners.add(listener);
    this.ownedByScreen = true;
    this.render();
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
      this.ownedByScreen = this.listeners.size > 0;
      this.render();
    };
  }

  private render(): void {
    this.element.style.display = this.state === 'online' || this.ownedByScreen ? 'none' : 'block';
  }
}
